import { useEffect, useMemo, useState } from 'react'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  Timestamp,
  updateDoc,
} from 'firebase/firestore'
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Search as SearchIcon } from '@mui/icons-material'
import { db } from '../../firebase'
import { paginate } from './subscriptionHelpers'

type CouponDoc = {
  id: string
  code?: string
  discountType?: 'percentage' | 'fixed_amount'
  discountPercent?: number
  discountAmountEgp?: number
  enabled?: boolean
  maxRedemptions?: number | null
  redemptionCount?: number
  validFrom?: any
  validUntil?: any
}

function toDateInput(value: any): string {
  if (!value) return ''
  const date = value.toDate ? value.toDate() : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 10)
}

export default function SubscriptionCouponsPage() {
  const [coupons, setCoupons] = useState<CouponDoc[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [status, setStatus] = useState<'all' | 'enabled' | 'disabled'>('all')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [couponDialog, setCouponDialog] = useState(false)
  const [couponForm, setCouponForm] = useState({
    id: '',
    code: '',
    discountType: 'percentage' as 'percentage' | 'fixed_amount',
    discountPercent: '10',
    discountAmountEgp: '10',
    maxRedemptions: '',
    validFrom: '',
    validUntil: '',
    enabled: true,
  })
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  })

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'subscription_coupons'), (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as CouponDoc[]
      rows.sort((a, b) => (a.code || '').localeCompare(b.code || ''))
      setCoupons(rows)
    })
    return () => unsub()
  }, [])

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toUpperCase()
    return coupons.filter((coupon) => {
      const enabled = coupon.enabled !== false
      if (status === 'enabled' && !enabled) return false
      if (status === 'disabled' && enabled) return false
      if (!q) return true
      return (coupon.code || '').toUpperCase().includes(q)
    })
  }, [coupons, searchQuery, status])

  useEffect(() => {
    setPage(0)
    setSelectedIds([])
  }, [searchQuery, status])

  const pageRows = paginate(filtered, page, rowsPerPage)
  const selectedOnPage = pageRows.filter((row) => selectedIds.includes(row.id))
  const notify = (message: string, severity: typeof snackbar.severity) =>
    setSnackbar({ open: true, message, severity })

  const openNewCoupon = () => {
    const from = new Date()
    const until = new Date()
    until.setFullYear(until.getFullYear() + 1)
    setCouponForm({
      id: '',
      code: '',
      discountType: 'percentage',
      discountPercent: '10',
      discountAmountEgp: '10',
      maxRedemptions: '',
      validFrom: from.toISOString().slice(0, 10),
      validUntil: until.toISOString().slice(0, 10),
      enabled: true,
    })
    setCouponDialog(true)
  }

  const saveCoupon = async () => {
    const code = couponForm.code.trim().toUpperCase()
    if (!/^[A-Z0-9_-]{2,40}$/.test(code)) {
      notify('كود الكوبون غير صالح', 'error')
      return
    }
    const payload: any = {
      code,
      discountType: couponForm.discountType,
      enabled: couponForm.enabled,
      maxRedemptions: couponForm.maxRedemptions ? Number(couponForm.maxRedemptions) : null,
      validFrom: Timestamp.fromDate(new Date(couponForm.validFrom)),
      validUntil: Timestamp.fromDate(new Date(`${couponForm.validUntil}T23:59:59`)),
      updatedAt: Timestamp.now(),
    }
    if (couponForm.discountType === 'percentage') {
      payload.discountPercent = Math.min(100, Math.max(1, Number(couponForm.discountPercent) || 1))
      payload.discountAmountEgp = null
    } else {
      payload.discountAmountEgp = Math.max(1, Number(couponForm.discountAmountEgp) || 1)
      payload.discountPercent = null
    }
    try {
      if (couponForm.id) {
        await updateDoc(doc(db, 'subscription_coupons', couponForm.id), payload)
      } else {
        await addDoc(collection(db, 'subscription_coupons'), {
          ...payload,
          redemptionCount: 0,
          createdAt: Timestamp.now(),
        })
      }
      setCouponDialog(false)
      notify('تم حفظ الكوبون', 'success')
    } catch {
      notify('تعذر حفظ الكوبون', 'error')
    }
  }

  const setEnabled = async (ids: string[], enabled: boolean) => {
    try {
      for (const id of ids) {
        await updateDoc(doc(db, 'subscription_coupons', id), {
          enabled,
          updatedAt: Timestamp.now(),
        })
      }
      notify(enabled ? 'تم تفعيل الكوبونات' : 'تم إيقاف الكوبونات', 'success')
      setSelectedIds([])
    } catch {
      notify('تعذر تحديث الكوبونات', 'error')
    }
  }

  const deleteSelected = async () => {
    try {
      for (const id of selectedIds) {
        await deleteDoc(doc(db, 'subscription_coupons', id))
      }
      notify('تم حذف الكوبونات المحددة', 'success')
      setSelectedIds([])
    } catch {
      notify('تعذر حذف الكوبونات', 'error')
    }
  }

  const toggleAllPage = (checked: boolean) => {
    if (checked) {
      setSelectedIds(Array.from(new Set([...selectedIds, ...pageRows.map((row) => row.id)])))
    } else {
      const pageIds = new Set(pageRows.map((row) => row.id))
      setSelectedIds(selectedIds.filter((id) => !pageIds.has(id)))
    }
  }

  return (
    <Stack spacing={2.5}>
      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <TextField
            placeholder="بحث بكود الكوبون"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>الحالة</InputLabel>
            <Select
              label="الحالة"
              value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}
            >
              <MenuItem value="all">الكل</MenuItem>
              <MenuItem value="enabled">مفعّل</MenuItem>
              <MenuItem value="disabled">متوقف</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" onClick={openNewCoupon}>
            كوبون جديد
          </Button>
        </Stack>
      </Paper>

      {selectedIds.length > 0 && (
        <Paper sx={{ p: 1.5, bgcolor: 'rgba(15,118,110,0.06)' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Typography sx={{ flex: 1, fontWeight: 600 }}>تم تحديد {selectedIds.length} كوبون</Typography>
            <Button size="small" onClick={() => setEnabled(selectedIds, true)}>
              تفعيل
            </Button>
            <Button size="small" onClick={() => setEnabled(selectedIds, false)}>
              إيقاف
            </Button>
            <Button size="small" color="error" onClick={deleteSelected}>
              حذف
            </Button>
            <Button size="small" onClick={() => setSelectedIds([])}>
              إلغاء التحديد
            </Button>
          </Stack>
        </Paper>
      )}

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={pageRows.length > 0 && selectedOnPage.length === pageRows.length}
                  indeterminate={selectedOnPage.length > 0 && selectedOnPage.length < pageRows.length}
                  onChange={(e) => toggleAllPage(e.target.checked)}
                />
              </TableCell>
              <TableCell>الكود</TableCell>
              <TableCell>الخصم</TableCell>
              <TableCell>الاستخدام</TableCell>
              <TableCell>مفعّل</TableCell>
              <TableCell>إجراء</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((coupon) => (
              <TableRow key={coupon.id} hover selected={selectedIds.includes(coupon.id)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedIds.includes(coupon.id)}
                    onChange={() =>
                      setSelectedIds((prev) =>
                        prev.includes(coupon.id)
                          ? prev.filter((id) => id !== coupon.id)
                          : [...prev, coupon.id],
                      )
                    }
                  />
                </TableCell>
                <TableCell>
                  <Typography fontWeight={700} sx={{ letterSpacing: 0.4 }}>
                    {coupon.code}
                  </Typography>
                </TableCell>
                <TableCell>
                  {coupon.discountType === 'percentage'
                    ? `${coupon.discountPercent || 0}%`
                    : `${coupon.discountAmountEgp || 0} جنيه`}
                </TableCell>
                <TableCell>
                  {coupon.redemptionCount || 0}
                  {coupon.maxRedemptions != null ? ` / ${coupon.maxRedemptions}` : ''}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      checked={coupon.enabled !== false}
                      onChange={(e) => setEnabled([coupon.id], e.target.checked)}
                    />
                    <Chip
                      size="small"
                      label={coupon.enabled === false ? 'متوقف' : 'مفعّل'}
                      color={coupon.enabled === false ? 'default' : 'success'}
                    />
                  </Stack>
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    onClick={() => {
                      setCouponForm({
                        id: coupon.id,
                        code: coupon.code || '',
                        discountType: coupon.discountType || 'percentage',
                        discountPercent: String(coupon.discountPercent || 10),
                        discountAmountEgp: String(coupon.discountAmountEgp || 10),
                        maxRedemptions:
                          coupon.maxRedemptions != null ? String(coupon.maxRedemptions) : '',
                        validFrom: toDateInput(coupon.validFrom),
                        validUntil: toDateInput(coupon.validUntil),
                        enabled: coupon.enabled !== false,
                      })
                      setCouponDialog(true)
                    }}
                  >
                    تعديل
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {pageRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">لا توجد كوبونات مطابقة</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="عدد الصفوف"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} من ${count}`}
        />
      </TableContainer>

      <Dialog open={couponDialog} onClose={() => setCouponDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{couponForm.id ? 'تعديل كوبون' : 'كوبون جديد'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="الكود"
              value={couponForm.code}
              onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>نوع الخصم</InputLabel>
              <Select
                label="نوع الخصم"
                value={couponForm.discountType}
                onChange={(e) =>
                  setCouponForm({
                    ...couponForm,
                    discountType: e.target.value as 'percentage' | 'fixed_amount',
                  })
                }
              >
                <MenuItem value="percentage">نسبة مئوية</MenuItem>
                <MenuItem value="fixed_amount">مبلغ ثابت</MenuItem>
              </Select>
            </FormControl>
            {couponForm.discountType === 'percentage' ? (
              <TextField
                label="النسبة %"
                type="number"
                value={couponForm.discountPercent}
                onChange={(e) => setCouponForm({ ...couponForm, discountPercent: e.target.value })}
              />
            ) : (
              <TextField
                label="الخصم بالجنيه"
                type="number"
                value={couponForm.discountAmountEgp}
                onChange={(e) =>
                  setCouponForm({ ...couponForm, discountAmountEgp: e.target.value })
                }
              />
            )}
            <TextField
              label="حد أقصى للاستخدام (اختياري)"
              value={couponForm.maxRedemptions}
              onChange={(e) => setCouponForm({ ...couponForm, maxRedemptions: e.target.value })}
            />
            <TextField
              label="من تاريخ"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={couponForm.validFrom}
              onChange={(e) => setCouponForm({ ...couponForm, validFrom: e.target.value })}
            />
            <TextField
              label="إلى تاريخ"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={couponForm.validUntil}
              onChange={(e) => setCouponForm({ ...couponForm, validUntil: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCouponDialog(false)}>إلغاء</Button>
          <Button variant="contained" onClick={saveCoupon}>
            حفظ
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  )
}
